import type { Account } from '../../types/account';
import type { AccountCampaign } from '../../types/accountCampaign';
import type { Campaign, CtaOption, MetaAdCopy, UtmConfig } from '../../types/campaign';
import type { FranchiseLocationProfile } from '../../types/franchiseLocationProfile';
import type { FranchiseProfile } from '../../types/franchiseProfile';
import type { MetaAdDraftInput, MetaCallToAction, UtmParams } from '../../types/metaAdDraft';
import type { Rollout } from '../../types/rollout';
import type { SavedAudience } from '../../types/savedAudience';
import { computeDailyBudget } from '../../lib/dailyBudget';
import { effectiveMetaAdType } from '../../lib/effectiveMetaAdType';
import { resolveRowLandingUrl } from '../../lib/landingPageResolution';
import { resolveRowMetaName } from '../../lib/resolveRowTemplates';
import { instantIsoToWallClock } from '../../lib/scheduleTime';
import {
  buildRowRenderContext,
  renderRowTemplate,
  renderRowTemplateArray,
  type RowRenderContext,
} from '../../lib/rowContent';
import { emptyAdSet, emptyMetaAdDraftInput } from './emptyDraft';
import { setPlacementMode } from './placements';
import { applySavedAudience } from './savedAudience';

// Build Campaign — Phase 1. Pure assembler that turns a rollout's Meta Build row
// into a fully-formed MetaAdDraftInput, ready to POST to /api/meta-ads/drafts and
// open in the builder pre-filled. No I/O — the caller fetches the account-default
// audiences + creates the draft + navigates. See docs/build-campaign-phase1-spec.md.
//
// Reuses the REAL resolvers the Build view already runs (resolveRowMetaName,
// computeDailyBudget, effectiveMetaAdType, resolveRowLandingUrl, the row render
// context) so the seeded draft matches exactly what the AM sees on the rollout.
//
// Branches on the effective Meta ad type:
//   - 'lead_gen' (Meta Lead Gen) → instant-form objective + the account default
//     lead form; no landing URL.
//   - 'traffic'  (Website Lead)  → website-conversion objective, landing URL,
//     conversion event = LEAD, UTM config; no lead form.
//   - null (unresolved)          → seed everything else; the AM picks the type in
//     the builder. Never blocks.
//
// NO images (Phase 2). Ads carry the rollout copy + destination so they're the
// default for the AM's creative, and P2 image-ads inherit them.

// ---------- CTA mapping (CtaOption → Zernio's MetaCallToAction) ----------
//
// All nine rollout CtaOption values are mapped explicitly. `exact` is false ONLY
// for a CTA with no Zernio equivalent (Zernio's enum omits BOOK_NOW / APPLY_NOW /
// CALL_NOW / SEND_MESSAGE), which falls back to SIGN_UP — and the assembler flags
// it so the AM never gets a silent wrong button. 'Get In Touch' → CONTACT_US,
// 'Apply Now' → APPLY are clean semantic equivalents (exact).

type CtaMapEntry = { value: MetaCallToAction; exact: boolean };

export const CTA_OPTION_TO_META: Record<CtaOption, CtaMapEntry> = {
  'Get In Touch': { value: 'CONTACT_US', exact: true },
  'Get Offer': { value: 'GET_OFFER', exact: true },
  'Get Quote': { value: 'GET_QUOTE', exact: true },
  'Learn More': { value: 'LEARN_MORE', exact: true },
  'Sign Up': { value: 'SIGN_UP', exact: true },
  // No Zernio BOOK_NOW (BOOK_TRAVEL is travel-specific, not a fit) — genuine
  // fallback, flagged.
  'Book Now': { value: 'SIGN_UP', exact: false },
  Subscribe: { value: 'SUBSCRIBE', exact: true },
  'Apply Now': { value: 'APPLY', exact: true },
  Download: { value: 'DOWNLOAD', exact: true },
};

export function mapCtaOption(cta: CtaOption | null | undefined): CtaMapEntry {
  if (cta && Object.prototype.hasOwnProperty.call(CTA_OPTION_TO_META, cta)) {
    return CTA_OPTION_TO_META[cta];
  }
  // Unknown/absent CTA → safe default, flagged.
  return { value: 'SIGN_UP', exact: false };
}

// ---------- UTM mapping (rollout UtmConfig → flat draft UtmParams) ----------
//
// Mirrors src/lib/utmQueryString.ts's resolution rules: disabled / platform not
// meta / no meta block / all-empty-after-resolve → null (caller keeps the builder's
// default utmParams). utm_campaign ← shared.campaignName ?? shared.campaignId. Each
// field passes through renderRowTemplate so per-location placeholders resolve.

export function rolloutUtmToUtmParams(
  utm: UtmConfig | null | undefined,
  ctx: RowRenderContext,
): UtmParams | null {
  if (!utm || !utm.enabled) return null;
  if (!utm.platforms.includes('meta')) return null;
  const config = utm.meta;
  if (!config) return null;

  const sharedCampaign = utm.shared.campaignName ?? utm.shared.campaignId ?? '';
  const params: UtmParams = {
    source: renderRowTemplate(config.source, ctx),
    medium: renderRowTemplate(config.medium, ctx),
    campaign: renderRowTemplate(sharedCampaign, ctx),
    content: renderRowTemplate(config.content, ctx),
    term: renderRowTemplate(config.term, ctx),
  };
  const anyValue = [params.source, params.medium, params.campaign, params.content, params.term].some(
    (v) => v.length > 0,
  );
  return anyValue ? params : null;
}

// ---------- schedule (rollout ISO timestamp → builder wall-clock) ----------
//
// The builder stores schedule as a wall-clock 'YYYY-MM-DDTHH:mm' (no tz), resolved
// against the AD-ACCOUNT timezone at the publish boundary (src/lib/scheduleTime.ts) —
// the same tz it displays the schedule hint in. The rollout's startDate/endDate are
// absolute IsoTimestamps (UTC). Slicing the raw UTC characters showed the UTC wall-
// clock, off by the account's offset (e.g. AEST seeded 10h early). CONVERT the
// instant into the ad-account tz FIRST, then take the wall-clock, so the seed matches
// what the rollout (and the builder) display. timeZone null → UTC fallback.

export function isoToScheduleWallClock(
  iso: string | null | undefined,
  timeZone: string | null,
): string | null {
  return instantIsoToWallClock(iso, timeZone);
}

// ---------- the assembler ----------

export type BuildCampaignDraftArgs = {
  rollout: Pick<Rollout, 'id' | 'monthYearLabel'>;
  row: AccountCampaign;
  account: Pick<
    Account,
    | 'name'
    | 'slug'
    | 'defaultPixelId'
    | 'defaultLeadFormId'
    | 'defaultInstagramAccountId'
    | 'defaultInstagramAccountName'
  >;
  campaign: Campaign | null;
  locationProfile: FranchiseLocationProfile | null;
  brandProfile: FranchiseProfile | null;
  // The account-default saved audiences (caller fetches + filters isAccountDefault).
  // One ad set is seeded per audience. Empty is blocked upstream; if empty here the
  // assembler leaves the single empty default ad set untouched (defensive).
  defaultAudiences: SavedAudience[];
  // The ad account's IANA timezone (e.g. 'Australia/Sydney' / 'Pacific/Auckland') —
  // the basis the builder resolves its wall-clock schedule against. The caller
  // resolves it from the linked ad account (useLinkedAdAccount.timezoneName), falling
  // back to the browser tz when unknown. The rollout's UTC start/end are converted
  // into THIS tz before being sliced to wall-clock, so the seed matches the rollout.
  scheduleTimeZone: string | null;
};

export type BuildCampaignDraftResult = {
  input: MetaAdDraftInput;
  // Non-blocking heads-up flags (missing defaults, unresolved fields, CTA fallback)
  // surfaced to the AM before opening the builder. NEVER fails the build.
  warnings: string[];
};

export function buildCampaignDraftFromRollout(
  args: BuildCampaignDraftArgs,
): BuildCampaignDraftResult {
  const { rollout, row, account, campaign, locationProfile, brandProfile, defaultAudiences, scheduleTimeZone } =
    args;
  const warnings: string[] = [];

  const renderCtx = buildRowRenderContext({
    row,
    campaign,
    account,
    locationProfile,
    brandProfile,
    rollout,
  });

  const input = emptyMetaAdDraftInput();
  input.accountId = row.accountId;

  // Title = the resolved Meta campaign name (verbatim — same resolver the Build
  // view renders). Falls back to the empty-draft title + a flag when no template.
  const resolvedName = resolveRowMetaName({
    row: {
      campaignDescriptor: row.campaignDescriptor,
      metaCampaignNameTemplate: row.metaCampaignNameTemplate,
      googleAdLabelTemplate: row.googleAdLabelTemplate,
    },
    campaign,
    account,
    locationProfile,
    brandProfile,
    rollout,
  }).resolved;
  if (resolvedName && resolvedName.trim()) {
    input.title = resolvedName;
  } else {
    warnings.push('Campaign name unresolved (no naming template) — set a title in the builder.');
  }

  // Campaign: CBO daily budget + datetime schedule + MANUAL placements.
  input.campaign.objective = 'LEADS';
  input.campaign.budgetMode = 'CBO';
  const budget = computeDailyBudget(row, 'meta');
  if ('perDayCents' in budget) {
    input.campaign.budget = { amountCents: budget.perDayCents, type: 'daily' };
  } else {
    input.campaign.budget = null;
    warnings.push('Daily budget not set (rollout is missing dates or a budget) — set it in the builder.');
  }
  input.campaign.defaultStartDate = isoToScheduleWallClock(row.startDate, scheduleTimeZone);
  input.campaign.defaultEndDate = isoToScheduleWallClock(row.endDate, scheduleTimeZone);
  // Reuse the builder's own "switch to manual" path so the seed inherits whatever
  // default manual selection (the Common set) the builder applies — one source of
  // truth, so changing that default later is inherited automatically.
  input.campaign.placements = setPlacementMode('manual');

  // Pixel — auto-assigned from the account default on BOTH paths.
  const defaultPixelId = account.defaultPixelId ?? null;
  input.ads.pixelId = defaultPixelId;
  if (!defaultPixelId) {
    warnings.push('No default pixel on the account — set it in the builder or Meta Setup.');
  }

  // Instagram identity — auto-assigned from the account default (the page-linked IG
  // captured at onboarding), same as manual compose's igDefaultPatch. REQUIRED for
  // asset-customized (feed+story pair) ads: Zernio only auto-resolves the page's IG on
  // the simple creative, so a paired ad with no explicit IG publishes WITHOUT an
  // Instagram identity (single ads keep it). Carrying it on the draft fixes that.
  const defaultIgId = account.defaultInstagramAccountId ?? null;
  if (defaultIgId) {
    input.ads.instagramAccountId = defaultIgId;
    input.ads.instagramAccountName = account.defaultInstagramAccountName ?? null;
  } else {
    warnings.push(
      'No default Instagram account on the account — paired (feed+story) ads may publish without an Instagram identity. Set it in Meta Setup.',
    );
  }

  // Branch on the effective Meta ad type.
  const adType = effectiveMetaAdType(
    { metaAdType: row.metaAdType ?? null },
    // effectiveMetaAdType needs campaign.metaAdCopy.metaAdType; with no campaign,
    // pass 'both' so it falls through to the row override / location default / null.
    campaign ? { metaAdCopy: campaign.metaAdCopy } : { metaAdCopy: { metaAdType: 'both' } },
    locationProfile,
  );
  const defaultLeadFormId = account.defaultLeadFormId ?? null;

  if (adType === 'lead_gen') {
    input.campaign.destinationType = 'instant_form';
    input.campaign.optimizationGoal = 'LEAD_GENERATION';
    input.campaign.customEventType = null;
    input.ads.destination = {
      type: 'instant_form',
      leadGenFormId: defaultLeadFormId ?? '',
      leadGenFormName: '',
    };
    if (!defaultLeadFormId) {
      warnings.push('No default lead form on the account — set it in the builder or Meta Setup.');
    }
  } else if (adType === 'traffic') {
    input.campaign.destinationType = 'website';
    input.campaign.optimizationGoal = 'OFFSITE_CONVERSIONS';
    input.campaign.customEventType = 'LEAD'; // Conversions event = Lead
    const landingUrl =
      renderRowTemplate(resolveRowLandingUrl(row, brandProfile, locationProfile, 'meta'), renderCtx) ||
      null;
    input.ads.destination = { type: 'website', url: landingUrl ?? '' };
    if (!landingUrl) {
      warnings.push('No landing URL resolved — set the website destination in the builder.');
    }
    const utmParams = rolloutUtmToUtmParams(row.utmConfig, renderCtx);
    if (utmParams) input.ads.utmParams = utmParams; // else keep the builder default
  } else {
    // Unresolved ad type — seed everything else; the AM picks the type in the
    // builder. Leave the empty-draft instant-form default + carry the lead form.
    input.ads.destination = {
      type: 'instant_form',
      leadGenFormId: defaultLeadFormId ?? '',
      leadGenFormName: '',
    };
    warnings.push('Meta ad type unresolved — pick an ad type in the builder.');
  }

  // Copy — carried on the shared ad (templated). Both paths; P2 image-ads inherit.
  const adCopy: MetaAdCopy | null = row.metaAdCopy;
  if (adCopy) {
    const primaryTexts = renderRowTemplateArray(adCopy.bodyCopy, renderCtx);
    const headlines = renderRowTemplateArray(adCopy.headlines, renderCtx);
    const descriptions = renderRowTemplateArray(adCopy.descriptions, renderCtx);
    // The multi-input fields need ≥1 entry to render an input row; coalesce empties
    // to the empty-draft default. Descriptions are 0–4 (an empty single row is fine).
    input.ads.creative.primaryTexts = primaryTexts.length ? primaryTexts : [''];
    input.ads.creative.headlines = headlines.length ? headlines : [''];
    input.ads.creative.descriptions = descriptions.length ? descriptions : [''];
    const cta = mapCtaOption(adCopy.ctaButton);
    input.ads.creative.callToAction = cta.value;
    if (!cta.exact) {
      warnings.push(
        `CTA "${adCopy.ctaButton}" has no Meta equivalent — defaulted to Sign Up; adjust in the builder.`,
      );
    }
  } else {
    warnings.push('No Meta ad copy on the rollout — add copy in the builder.');
  }

  // Build Campaign Phase 2: snapshot the rollout's Drive folders + asset labels onto
  // the draft so the builder's "Pull creative from Drive" action is self-contained
  // (no reach-back into the rollouts module). Folders are listed live at pull time;
  // labels (the authoritative feed/story adType) are this snapshot.
  input.driveSource = {
    rolloutId: rollout.id,
    rowId: row.id,
    folders: {
      national: row.metaAssetsFolderUrl ?? null,
      local: row.localAssetsUrl ?? null,
      organic: row.organicAssetsUrl ?? null,
    },
    labels: {
      national: row.metaAssetLabels ?? [],
      local: row.localAssetLabels ?? [],
      organic: row.organicAssetLabels ?? [],
    },
  };

  // Ad sets — ONE per account-default audience, named after it. Targeting applied
  // via applySavedAudience (extracted audiences stay verbatim via rawPayload). No
  // per-ad-set budget (CBO). The audience id doubles as the stable ad-set id (one
  // ad set per audience → unique + deterministic).
  if (defaultAudiences.length > 0) {
    input.adSets = defaultAudiences.map((aud) => {
      const seeded = applySavedAudience(emptyAdSet(aud.id), aud);
      return { ...seeded, name: aud.name, budget: null };
    });
  }

  return { input, warnings };
}
